import { useState } from 'react';
import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Smartphone } from 'lucide-react';

import { api } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { DRIVER_STATUS_LABELS, DRIVER_STATUS_VARIANT, fmtDay, licenseAlert } from '@/lib/drivers';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';

import type { Driver, Paginated } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/drivers')({
  beforeLoad: () => requirePermission('vehicles.read'),
  component: DriversPage,
});

function DriversPage() {
  const matchRoute = useMatchRoute();
  const isSubRoute = !!(matchRoute({ to: '/drivers/new' }) || matchRoute({ to: '/drivers/$id' }));
  if (isSubRoute) return <Outlet />;
  return <DriversList />;
}

function DriversList() {
  const [search, setSearch] = useState('');
  // Conducteurs gérés par fleet-service sous vehicles.* (pas drivers.*)
  const canWrite = useHasPermission('vehicles.write');

  const query = useQuery({
    queryKey: ['drivers', search],
    queryFn: async () =>
      (await api.get<Paginated<Driver>>(`/drivers?search=${encodeURIComponent(search)}&pageSize=100`)).data,
  });
  const { data, isLoading } = query;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Conducteurs</h1>
        {canWrite && (
          <Button asChild>
            <Link to="/drivers/new">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un conducteur
            </Link>
          </Button>
        )}
      </div>
      <QueryError query={query} />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par nom, permis, téléphone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>N° de permis</TableHead>
              <TableHead>Expiration permis</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>App chauffeur</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {data?.data?.map((d) => {
              const alert = licenseAlert(d.licenseExpiry);
              return (
                <TableRow key={d.id}>
                  <TableCell className="font-semibold">{d.firstName} {d.lastName}</TableCell>
                  <TableCell className="font-mono text-xs">{d.licenseNumber}</TableCell>
                  <TableCell>
                    {fmtDay(d.licenseExpiry)}
                    {alert && (
                      <Badge variant={alert === 'expired' ? 'destructive' : 'warning'} className="ml-2">
                        {alert === 'expired' ? 'Expiré' : 'Bientôt'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{d.phone ?? '—'}</TableCell>
                  <TableCell>
                    {d.userId
                      ? <span className="inline-flex items-center gap-1 text-success"><Smartphone className="h-4 w-4" /> Connecté</span>
                      : <span className="text-muted-foreground">Non activé</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={DRIVER_STATUS_VARIANT[d.status]}>{DRIVER_STATUS_LABELS[d.status] ?? d.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/drivers/$id" params={{ id: d.id }}>Détail</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {data && (data.data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Aucun conducteur trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
